/* import __COLOCATED_TEMPLATE__ from './track-people-typing.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type Nexus from 'embercom/services/nexus';
import type BpoAccessControlService from 'embercom/services/bpo-access-control';
import {
  NexusEventName,
  type UserIsTypingEvent,
  type AdminIsTypingANoteEvent,
  type AdminIsTypingEvent,
} from 'embercom/services/nexus';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { timeout } from 'ember-concurrency';
import ENV from 'embercom/config/environment';
import { taskFor } from 'ember-concurrency-ts';
import AdminSummary from 'embercom/objects/inbox/admin-summary';
import type Session from 'embercom/services/session';

import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';

interface Signature {
  Args: {
    conversationId: number;
  };
  Blocks: {
    default: [
      {
        typingUserId?: string;
        typingAdmin?: AdminSummary;
        typingNoteAdmin?: AdminSummary;
      },
    ];
  };
}

const TIMEOUT = ENV.APP._400MS * 3;

type TypingEvent = UserIsTypingEvent | AdminIsTypingEvent | AdminIsTypingANoteEvent;

export default class TrackPeopleTypingComponent extends Component<Signature> {
  @service declare nexus: Nexus;
  @service declare bpoAccessControl: BpoAccessControlService;
  @service declare session: Session;

  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;

  conversationId?: number;

  @tracked typingUserId?: string;
  @tracked typingAdmin?: AdminSummary;
  @tracked typingNoteAdmin?: AdminSummary;

  get hasNoTypingEvents() {
    return !this.typingUserId && !this.typingAdmin && !this.typingNoteAdmin;
  }

  get events() {
    return [
      NexusEventName.UserIsTyping,
      NexusEventName.AdminIsTyping,
      NexusEventName.AdminIsTypingANote,
    ];
  }

  private resolveAndSetTypingAdmin(
    event: AdminIsTypingEvent | AdminIsTypingANoteEvent,
    currentAdmin: AdminSummary | undefined,
    setAdmin: (admin: AdminSummary) => void,
  ): void {
    let id = Number(event.eventData.adminId);

    // Early return if the admin hasn't changed
    if (currentAdmin?.id === id) {
      return;
    }

    if (!this.session.workspace.isFeatureEnabled('hsg-bpo-separation-websocket-redaction')) {
      setAdmin(
        AdminSummary.deserialize({
          id,
          name: event.eventData.adminName,
          image_url: event.eventData.adminAvatar,
        }),
      );
    } else {
      // Only use admins that are accessible through the assignee search service
      let admin = this.inbox2AssigneeSearch.findAdminById(id);
      if (admin) {
        setAdmin(admin);
      }
    }
  }

  @task({ restartable: true }) *userIsTypingTimer(event: UserIsTypingEvent) {
    if (this.typingUserId !== event['nx.FromUser']) {
      this.typingUserId = event['nx.FromUser'];
    }
    yield timeout(TIMEOUT);
    this.typingUserId = undefined;
  }

  @task({ restartable: true }) *adminIsTypingTimer(event: AdminIsTypingEvent) {
    let adminId = Number(event.eventData.adminId);

    // Filter out events from admins that shouldn't be visible due to BPO separation
    if (!this.bpoAccessControl.adminAccessible(adminId)) {
      return;
    }

    this.resolveAndSetTypingAdmin(event, this.typingAdmin, (admin) => {
      this.typingAdmin = admin;
    });
    yield timeout(TIMEOUT);
    this.typingAdmin = undefined;
  }

  @task({ restartable: true }) *adminIsTypingANoteTimer(event: AdminIsTypingANoteEvent) {
    let adminId = Number(event.eventData.adminId);

    // Filter out events from admins that shouldn't be visible due to BPO separation
    if (!this.bpoAccessControl.adminAccessible(adminId)) {
      return;
    }

    this.resolveAndSetTypingAdmin(event, this.typingNoteAdmin, (admin) => {
      this.typingNoteAdmin = admin;
    });
    yield timeout(TIMEOUT);
    this.typingNoteAdmin = undefined;
  }

  @action
  handleTypingEvent(event: TypingEvent) {
    let eventData = event.eventData;

    if (Number(eventData.conversationId) === this.args.conversationId) {
      switch (event.eventName) {
        case NexusEventName.UserIsTyping: {
          taskFor(this.userIsTypingTimer).perform(event);
          break;
        }
        case NexusEventName.AdminIsTyping: {
          taskFor(this.adminIsTypingTimer).perform(event);
          break;
        }
        case NexusEventName.AdminIsTypingANote: {
          taskFor(this.adminIsTypingANoteTimer).perform(event);
          break;
        }
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Conversation::TrackPeopleTyping': typeof TrackPeopleTypingComponent;
  }
}
